import { useState, useEffect, useMemo } from 'react'
import { FileText, X as XIcon, RefreshCw, HelpCircle } from 'lucide-react'
import Lottie from 'lottie-react'
import LoaderAdm from '../../../../../../../assets/svgIcons/LoaderAdm.json'
import ResizableModal from '../../../../../ui/ResizableModal'
import clsx from 'clsx'
import { FacturaAPI } from '../../../../../../../api/facturas.api'
import { sileo } from 'sileo'
import { getErrorMessage } from '../../utils/utils'

// Helper for CLP currency formatting
const formatMoney = amount => {
  if (!amount && amount !== 0) return ''
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(amount)
}

const formatSimpleDate = dateStr => {
  if (!dateStr) return '-'
  const [year, month, day] = dateStr.split('T')[0].split('-')
  return `${day}-${month}-${year}`
}

const formatMonthsEs = dateStr => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (isNaN(d)) return dateStr
  const day = d.getDate()
  const months = [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre'
  ]
  const month = months[d.getMonth()]
  const year = d.getFullYear()
  const hours = String(d.getHours()).padStart(2, '0')
  const mins = String(d.getMinutes()).padStart(2, '0')
  return `${day} de ${month}, ${year} a las ${hours}:${mins}`
}

export default function EstadoTrazaModal({ open, onClose, data }) {
  const facturaApi = useMemo(() => new FacturaAPI(), [])
  const [doc, setDoc] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (open && data?.id) {
      const fetchDoc = async () => {
        setIsLoading(true)
        try {
          const res = await facturaApi.getDocument(data.id)
          setDoc(res.data)
        } catch (err) {
          console.error('Error fetching document detail', err)
          sileo.error({
            title: 'Error de carga',
            description: getErrorMessage(err, 'No se pudo cargar la información del documento.')
          })
          onClose()
        } finally {
          setIsLoading(false)
        }
      }
      fetchDoc()
    } else if (!open) {
      // Limpiar al cerrar
      setDoc(null)
    }
  }, [open, data?.id, facturaApi])

  if (!open) return null

  const trace = doc?.traces && doc.traces.length > 0 ? doc.traces[0] : {}
  const states = doc?.document_states || []
  const hasMerito = states.some(s => s.state_type === 'executive_merit')

  return (
    <ResizableModal open={open} onClose={onClose} width={850} height={580}>
      <div
        className='w-full text-gray-800 font-sans p-6 px-8 rounded-lg relative overflow-y-auto'
        style={{ maxHeight: '90vh' }}
      >
        {/* Header (Perfil del Documento) */}
        <div className='flex justify-between items-center mb-6'>
          <h2 className='text-xl font-semibold'>Perfil del Documento</h2>
          <button onClick={onClose} className='text-gray-400 hover:text-gray-600 transition-colors'>
            <XIcon size={24} />
          </button>
        </div>

        {isLoading ? (
          <div className='flex flex-col items-center justify-center py-20'>
            <Lottie animationData={LoaderAdm} loop style={{ width: 120, height: 120 }} />
            <p className='text-sm text-gray-500 mt-4 animate-pulse'>Cargando trazabilidad...</p>
          </div>
        ) : doc ? (
          <>
            {/* Info Top Section */}
            <div className='flex flex-col md:flex-row justify-between items-start gap-4 mb-6'>
              <div className='flex flex-col gap-3 text-sm'>
                <div className='flex items-center gap-2'>
                  <span className='text-gray-600 font-medium w-16'>Folio:</span>
                  <span className='font-semibold text-gray-700'>#{doc.folio || '-'}</span>
                  <span className='bg-gray-100 text-gray-700 text-xs px-3 py-1 ml-2 rounded-full whitespace-nowrap'>
                    {doc.dte_type_description || 'Factura'}
                  </span>
                </div>
                <div className='flex items-start gap-2 text-[0.95rem]'>
                  <span className='text-gray-600 font-medium w-16 shrink-0'>Cliente:</span>
                  <span className='text-gray-800'>{doc.receiver_name || '-'}</span>
                </div>
                <div className='flex items-start gap-2 text-[0.95rem]'>
                  <span className='text-gray-600 font-medium w-16 shrink-0 mt-0.5'>Emisor:</span>
                  <span className='text-gray-800 max-w-[350px] leading-tight'>
                    {doc.sender_name || '-'}
                  </span>
                </div>
                <div className='flex items-center gap-2 text-[0.95rem]'>
                  <span className='text-gray-600 font-medium w-16'>Monto:</span>
                  <span className='font-bold text-gray-800'>
                    {formatMoney(doc.amount_with_iva)}
                  </span>
                </div>
                <div className='flex items-center gap-2 text-[0.95rem]'>
                  <span className='text-gray-600 font-medium w-16'>Fecha:</span>
                  <span className='text-gray-800'>{formatSimpleDate(doc.date_issued)}</span>
                </div>
              </div>

              <div className='flex items-center gap-6 self-end md:self-start md:mt-2 mt-4'>
                <button
                  className='text-red-500 hover:text-red-700 transition-colors'
                  title='Descargar PDF'
                  onClick={() => doc.pdf && window.open(doc.pdf, '_blank')}
                  disabled={!doc.pdf}
                >
                  <FileText size={28} strokeWidth={2} className={!doc.pdf ? 'opacity-30' : ''} />
                </button>
                <button className='bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm py-2.5 px-6 rounded-md flex items-center gap-2 transition-colors'>
                  <RefreshCw size={18} />
                  Actualizar Traza
                </button>
              </div>
            </div>

            {/* Divider */}
            <hr className='border-gray-100 my-8' />

            {/* Trazabilidad header */}
            <div className='flex items-center gap-2 mb-8'>
              <span className='font-semibold text-gray-800 text-sm'>Trazabilidad:</span>
              <button className='text-gray-400 hover:text-gray-600 flex items-center gap-1 text-[0.8rem] bg-transparent transition-colors'>
                <HelpCircle size={14} />
                <span>Guía de Eventos</span>
              </button>
            </div>

            {/* Resumen de Estado */}
            <h3 className='text-lg font-bold text-gray-800 mb-4'>Resumen de Estado</h3>

            <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8'>
              {/* Acuse Recibo */}
              <div className='border border-gray-100 bg-gray-50/50 rounded-xl p-4 flex flex-col justify-center'>
                <div className='text-xs text-gray-500 mb-2'>Acuse Recibo</div>
                <div className='flex items-center gap-1.5 font-bold text-gray-700 text-[0.9rem]'>
                  {trace.has_acknowledgments ? (
                    <span className='text-green-600'>✓ Sí</span>
                  ) : (
                    <>
                      <span className='text-gray-400 font-semibold text-base leading-none'>X</span>{' '}
                      No
                    </>
                  )}
                </div>
              </div>

              {/* Rechazado */}
              <div className='border border-gray-100 bg-gray-50/50 rounded-xl p-4 flex flex-col justify-center'>
                <div className='text-xs text-gray-500 mb-2'>Rechazado</div>
                <div className='flex items-center gap-1.5 font-bold text-gray-700 text-[0.9rem]'>
                  {trace.is_rejected ? (
                    <span className='text-red-600'>✓ Sí</span>
                  ) : (
                    <>
                      <span className='text-gray-400 font-semibold text-base leading-none'>X</span>{' '}
                      No
                    </>
                  )}
                </div>
              </div>

              {/* Reclamos */}
              <div className='border border-gray-100 bg-gray-50/50 rounded-xl p-4 flex flex-col justify-center'>
                <div className='text-xs text-gray-500 mb-2'>Reclamos</div>
                <div className='flex items-center gap-1.5 font-bold text-gray-700 text-[0.9rem]'>
                  {trace.has_claims ? (
                    <span className='text-red-600'>✓ Sí</span>
                  ) : (
                    <>
                      <span className='text-gray-400 font-semibold text-base leading-none'>X</span>{' '}
                      No
                    </>
                  )}
                </div>
              </div>

              {/* Mérito Ejecutivo */}
              <div
                className={clsx(
                  'border rounded-xl p-4 flex flex-col justify-center relative overflow-hidden',
                  hasMerito
                    ? 'border-green-200 bg-green-50/30'
                    : 'border-yellow-200 bg-yellow-50/40'
                )}
              >
                {/* Left colored border accent */}
                <div
                  className={clsx(
                    'absolute left-0 top-0 bottom-0 w-[5px]',
                    hasMerito ? 'bg-green-400' : 'bg-yellow-400'
                  )}
                ></div>

                <div className='text-xs text-gray-500 mb-2 ml-1 pl-1 font-medium'>
                  Mérito Ejecutivo
                </div>
                <div className='flex items-center gap-2 ml-1 pl-1'>
                  <div className='flex items-center gap-1.5 font-bold text-gray-700 text-[0.9rem]'>
                    {hasMerito ? (
                      <span className='text-green-600'>✓ Sí</span>
                    ) : (
                      <span className='text-yellow-600 flex items-center gap-1'>
                        <span className='text-yellow-600 font-semibold text-base leading-none'>
                          X
                        </span>{' '}
                        No
                      </span>
                    )}
                  </div>
                  {!hasMerito && trace.days_until_executive_merit > 0 && (
                    <span className='bg-yellow-100 text-yellow-800 text-[10px] px-2 py-0.5 rounded-full font-bold h-fit flex items-center'>
                      {trace.days_until_executive_merit} días
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Fecha de Recepción SII */}
            {trace.date_reception && (
              <div className='inline-block border border-green-200 bg-[#ebfcf1] rounded-xl p-4 min-w-[300px]'>
                <div className='text-[0.8rem] text-gray-500 mb-2 font-medium'>
                  Fecha de Recepción SII
                </div>
                <div className='font-semibold text-gray-800 text-[0.95rem]'>
                  {formatMonthsEs(trace.date_reception)}
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>
    </ResizableModal>
  )
}
